import React, { useState } from 'react';
import { db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import Layout from '../components/Layout';
import { logError } from '../utils/errorLogger';
import { cleanName } from '../utils/names';
import Papa from 'papaparse';
import {
  Box, Typography, Paper, Button,   Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, Chip, Divider, LinearProgress
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

const IMPORT_TYPES = [
  { id: 'members', label: 'Members', desc: 'Active athletes from PushPress', color: '#DEEAF1', textColor: '#1E3A5F' },
  { id: 'leads', label: 'Leads', desc: 'Potential members from PushPress', color: '#FEF9E7', textColor: '#7B4800' },
  { id: 'staff', label: 'Staff / Coaches', desc: 'Coaches and staff from PushPress', color: '#E9F7EF', textColor: '#1E6B3C' },
];

function Import() {
  const [importType, setImportType] = useState('members');
  const [parsed, setParsed] = useState([]);
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setResults(null);
    setError('');
    setParsed([]);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        if (result.errors.length > 0) {
          setError('Error reading CSV file. Please make sure it is a valid PushPress export.');
          return;
        }
        setParsed(result.data);
      }
    });
  };

  const mapMember = (row, index) => {
    const now = new Date();
    const athleteId = `ath_import_${Date.now()}_${index}`;
    const dob = row.dob || '';
    const grade = '';
    return {
      athleteId,
      parentUserId: null,
      firstName: cleanName(row.firstName),
      lastName: cleanName(row.lastName),
      email: row.email || '',
      phone: String(row.phone || '').replace(/\D/g, '').slice(-10),
      birthday: dob,
      athleteType: 'archived',
      isArchived: true,
      skillGroup: '',
      schoolName: '',
      currentGrade: grade,
      graduationYear: null,
      indoorExperience: '',
      beachExperience: '',
      shirtSize: '',
      goals: [],
      sessionBalance: 0,
      weeklyBalance: 0,
      address: row.address || '',
      city: row.city || '',
      state: row.state || '',
      zip: String(row.postalCode || ''),
      emergencyContact: {
        name: cleanName(row.emergencyContact),
        phone: String(row.emergencyContactPhone || '').replace(/\D/g, '').slice(-10),
        relationship: row.emergencyContactRelationship || '',
      },
      allergies: 'None',
      medicalConditions: 'None',
      waiverSigned: false,
      mediaWaiverSigned: false,
      waiverDate: null,
      registrationDate: now,
      importedFrom: 'pushpress',
      importedAt: now,
      pushpressId: row.memberId || '',
      planName: row.plan || '',
      planStatus: row.planStatus || '',
      memberSince: row.memberSince || '',
      status: row.status || 'active',
    };
  };

  const mapLead = (row, index) => {
    const now = new Date();
    const athleteId = `lead_import_${Date.now()}_${index}`;
    return {
      athleteId,
      parentUserId: null,
      firstName: cleanName(row.firstName),
      lastName: cleanName(row.lastName),
      email: row.email || '',
      phone: String(row.phone || '').replace(/\D/g, '').slice(-10),
      birthday: row.dob || '',
      athleteType: 'archived',
      isArchived: true,
      skillGroup: '',
      sessionBalance: 0,
      weeklyBalance: 0,
      goals: [],
      allergies: 'None',
      medicalConditions: 'None',
      waiverSigned: false,
      mediaWaiverSigned: false,
      waiverDate: null,
      registrationDate: now,
      importedFrom: 'pushpress_leads',
      importedAt: now,
      pushpressId: row.userUuid || '',
      status: row.status || 'lead',
      entryDate: row.entryDate || '',
      lastActivity: row.lastActivity || '',
    };
  };

  const mapStaff = (row, index) => {
    const now = new Date();
    const coachId = `coach_import_${Date.now()}_${index}`;
    return {
      coachId,
      firstName: cleanName(row.firstName),
      lastName: cleanName(row.lastName),
      email: row.email || '',
      role: row.role || 'coach',
      headRatePerHour: 25.00,
      assistantRatePerHour: 20.00,
      isVolunteer: false,
      totalSessionsLed: 0,
      totalAssistSessions: 0,
      totalOwed: 0,
      totalPaid: 0,
      importedFrom: 'pushpress_staff',
      importedAt: now,
    };
  };

  const handleImport = async () => {
    if (parsed.length === 0) return;
    setImporting(true);
    setProgress(0);
    setResults(null);

    const successful = [];
    const failed = [];

    for (let i = 0; i < parsed.length; i++) {
      try {
        const row = parsed[i];
        if (importType === 'members') {
          const athlete = mapMember(row, i);
          await setDoc(doc(db, 'athletes', athlete.athleteId), athlete);
          successful.push(`${row.firstName} ${row.lastName}`);
        } else if (importType === 'leads') {
          const lead = mapLead(row, i);
          await setDoc(doc(db, 'athletes', lead.athleteId), lead);
          successful.push(`${row.firstName} ${row.lastName}`);
        } else if (importType === 'staff') {
          const coach = mapStaff(row, i);
          await setDoc(doc(db, 'coaches', coach.coachId), coach);
          successful.push(`${row.firstName} ${row.lastName}`);
        }
      } catch (err) {
        logError('Import', err.message || String(err), err.stack || '');
        failed.push(`Row ${i + 1}: ${err.message}`);
      }
      setProgress(Math.round(((i + 1) / parsed.length) * 100));
    }

    setResults({ successful, failed });
    setImporting(false);
  };

  const resetImport = () => {
    setParsed([]);
    setFileName('');
    setResults(null);
    setError('');
    setProgress(0);
  };

  const previewColumns = {
    members: ['firstName', 'lastName', 'email', 'phone', 'dob', 'plan', 'status'],
    leads: ['firstName', 'lastName', 'email', 'phone', 'status', 'entryDate'],
    staff: ['firstName', 'lastName', 'email', 'role'],
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>
            Import from PushPress
          </Typography>
          <Typography variant="body1" sx={{ color: '#888', mt: 0.5 }}>
            Bulk import your existing members, leads, and staff from PushPress CSV exports.
          </Typography>
        </Box>

        {/* Import type selector */}
        <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
          {IMPORT_TYPES.map(type => (
            <Paper
              key={type.id}
              onClick={() => { setImportType(type.id); resetImport(); }}
              elevation={importType === type.id ? 4 : 1}
              sx={{
                p: 2.5, borderRadius: 2, cursor: 'pointer', minWidth: 180,
                border: importType === type.id ? `2px solid ${type.textColor}` : '2px solid transparent',
                backgroundColor: importType === type.id ? type.color : 'white',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: type.textColor }}>{type.label}</Typography>
              <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>{type.desc}</Typography>
            </Paper>
          ))}
        </Box>

        {/* Instructions */}
        <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3, backgroundColor: '#f8f9fa' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1E3A5F', mb: 1 }}>
            How to export from PushPress
          </Typography>
          <Typography variant="body2" sx={{ color: '#555' }}>
            1. Log into your PushPress admin dashboard
            {importType === 'members' && ' → Go to Members → Export → Download CSV'}
            {importType === 'leads' && ' → Go to Leads → Export → Download CSV'}
            {importType === 'staff' && ' → Go to Staff → Export → Download CSV'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#555', mt: 0.5 }}>
            2. Upload the downloaded CSV file below
          </Typography>
          <Typography variant="body2" sx={{ color: '#555', mt: 0.5 }}>
            3. Preview the data and click Import
          </Typography>
          {importType === 'members' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Session balances will be set to 0 after import. You can update each athlete's balance manually from their profile after importing.
            </Alert>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            Imported records will be tagged as <strong>Archived</strong> and will not appear as active athletes, parents, or leads. You can unarchive individual records from their profile after importing.
          </Alert>
        </Paper>

        {/* File upload */}
        {!results && (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1E3A5F', mb: 2 }}>
              Upload CSV File
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileIcon />}
              sx={{ mr: 2, borderColor: '#2E6DA4', color: '#2E6DA4' }}
            >
              Choose File
              <input type="file" accept=".csv" hidden onChange={handleFileUpload} />
            </Button>
            {fileName && (
              <Typography variant="body2" sx={{ display: 'inline', color: '#555' }}>
                {fileName}
              </Typography>
            )}
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </Paper>
        )}

        {/* Preview table */}
        {parsed.length > 0 && !results && (
          <Paper elevation={2} sx={{ borderRadius: 3, mb: 3 }}>
            <Box sx={{ p: 3, borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>
                Preview — {parsed.length} record{parsed.length !== 1 ? 's' : ''} found
              </Typography>
              <Chip label={`${parsed.length} to import`} color="primary" size="small" />
            </Box>
            <TableContainer sx={{ maxHeight: 320 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {previewColumns[importType].map(col => (
                      <TableCell key={col} sx={{ backgroundColor: '#1E3A5F', color: 'white', fontWeight: 'bold' }}>
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsed.map((row, i) => (
                    <TableRow key={i} sx={{ backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                      {previewColumns[importType].map(col => (
                        <TableCell key={col} sx={{ fontSize: '0.8rem' }}>
                          {String(row[col] || '—').substring(0, 40)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ p: 3, borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button variant="outlined" onClick={resetImport}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleImport}
                disabled={importing}
                sx={{ backgroundColor: '#27AE60', '&:hover': { backgroundColor: '#1E8449' } }}
              >
                {importing ? 'Importing...' : `Import ${parsed.length} Records`}
              </Button>
            </Box>
          </Paper>
        )}

        {/* Progress bar */}
        {importing && (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
              Importing... {progress}%
            </Typography>
            <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
          </Paper>
        )}

        {/* Results */}
        {results && (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1E3A5F', mb: 3 }}>
              Import Complete
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ color: '#27AE60' }} />
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#27AE60' }}>{results.successful.length}</Typography>
                <Typography variant="body2" sx={{ color: '#555' }}>imported successfully</Typography>
              </Box>
              {results.failed.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ErrorIcon sx={{ color: '#E74C3C' }} />
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#E74C3C' }}>{results.failed.length}</Typography>
                  <Typography variant="body2" sx={{ color: '#555' }}>failed</Typography>
                </Box>
              )}
            </Box>

            {results.failed.length > 0 && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#E74C3C', mb: 1 }}>Failed Records:</Typography>
                {results.failed.map((f, i) => (
                  <Typography key={i} variant="body2" sx={{ color: '#E74C3C' }}>{f}</Typography>
                ))}
              </>
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={resetImport}>Import Another File</Button>
              <Button variant="contained" href="/athletes" sx={{ backgroundColor: '#1E3A5F' }}>
                View Athletes
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
    </Layout>
  );
}

export default Import;
